/*
 * <cdx-product-tabs> – product page tabs (sections/tabbed-content.liquid): tabs on desktop, accordion
 * on mobile (< 990px). Also handles
 *   .cdx-rating-scroll-to-reviews  rating stars → open the tab with the reviews and scroll to it
 *   .scroll-to-tab[data-tab-type] buttons anywhere on the page → open the first tab of that type
 *   (data-tab-handle: open the tab with that handle)
 *   an anchor in the URL that points into a tab (e.g. #judgeme_product_reviews, #ContactForm after
 *   sending the product question) → open that tab on load
 */
if (!customElements.get("cdx-product-tabs")) {
  const isMobile = () => window.innerWidth < 990;

  customElements.define(
    "cdx-product-tabs",
    class CdxProductTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = this.querySelector("tabs-component");
        this.abortController = new AbortController();
        this.addEventListener(
          "click",
          (event) => {
            const header = event.target.closest(".cdx-mobile-accordion-header");
            if (header && isMobile()) this.toggleAccordion(header);
          },
          { signal: this.abortController.signal }
        );
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      content(handle) {
        return this.querySelector(`.tabs-component-content[data-handle="${CSS.escape(handle)}"]`);
      }

      toggleAccordion(header, open = !header.classList.contains("is-open")) {
        const content = this.content(header.dataset.accordionHandle);
        if (!content) return;
        header.classList.toggle("is-open", open);
        header.setAttribute("aria-expanded", String(open));
        content.classList.toggle("is-accordion-open", open);
      }

      // Opens a tab (desktop) or its accordion (mobile) and scrolls to it
      openTab(handle, { mobileExtra = 20, readjust = [] } = {}) {
        const content = this.content(handle);
        if (!content) return false;
        const scroll = window.CdxUtils?.scrollToElementWithOffset || (() => {});

        if (isMobile()) {
          const header = this.querySelector(
            `.cdx-mobile-accordion-header[data-accordion-handle="${CSS.escape(handle)}"]`
          );
          if (header) this.toggleAccordion(header, true);
          // readjust: the sticky header slides back in while scrolling up and would cover the target
          scroll(header || content, { extra: mobileExtra, readjust });
        } else {
          const nav = this.querySelector(`.pdp-tab-item[data-handle="${CSS.escape(handle)}"]`);
          if (!nav) return false;
          this.tabs?.openTab(handle);
          scroll(nav, { extra: 120, includeHeader: false });
        }
        return true;
      }

      openTabContaining(element) {
        const content = element?.closest(".tabs-component-content");
        if (!content || !this.contains(content)) return false;
        return this.openTab(content.dataset.handle, { mobileExtra: 24, readjust: [450, 900] });
      }
    }
  );

  const tabsFor = (element) => element?.closest("cdx-product-tabs");

  // Capture phase: runs before the generic in-page anchor scrolling (cdx-utils.js)
  document.addEventListener(
    "click",
    (event) => {
      const ratingLink = event.target.closest(".cdx-rating-scroll-to-reviews");
      if (ratingLink) {
        const target = document.querySelector(
          ratingLink.getAttribute("data-cdx-scroll-target") || "#judgeme_product_reviews"
        );
        // Not found: the link's own #judgeme_product_reviews href is the fallback
        if (tabsFor(target)?.openTabContaining(target)) event.preventDefault();
        return;
      }

      const tabButton = event.target.closest(".scroll-to-tab");
      if (tabButton) {
        event.preventDefault();
        const { tabType, tabHandle = "" } = tabButton.dataset;
        const content = tabType
          ? document.querySelector(`.tabs-component-content[data-tab-type="${CSS.escape(tabType)}"]`)
          : document.querySelector(`.tabs-component-content[data-handle="${CSS.escape(tabHandle)}"]`);
        if (content) tabsFor(content)?.openTab(content.dataset.handle);
      }
    },
    true
  );

  const openTabFromUrl = () => {
    let target = null;
    try {
      target = location.hash.length > 1 ? document.getElementById(decodeURIComponent(location.hash.slice(1))) : null;
    } catch (error) {
      target = null;
    }
    // Contact forms come back with ?contact_posted=…; show their status message
    if (!target && new URLSearchParams(location.search).has("contact_posted")) {
      target = document.querySelector(".tabs-component-content .form-status");
    }
    if (target) tabsFor(target)?.openTabContaining(target);
  };
  if (location.hash.length > 1 || location.search.includes("contact_posted")) setTimeout(openTabFromUrl, 250);
}
